import { LeadFormData, ValidationErrors, ValidationRule } from '../types';

/**
 * Default validation rules for lead forms
 */
export const DEFAULT_VALIDATION_RULES = {
  name: [
    { type: 'required' as const, message: 'Name is required' },
    { type: 'minLength' as const, value: 2, message: 'Name must be at least 2 characters' },
    { type: 'maxLength' as const, value: 100, message: 'Name must be less than 100 characters' }
  ],
  email: [
    { type: 'required' as const, message: 'Email is required' },
    { type: 'email' as const, message: 'Please enter a valid email address' }
  ],
  phone: [
    { type: 'pattern' as const, value: '^[+]?[\\d\\s\\-\\(\\)]+$', message: 'Please enter a valid phone number' }
  ],
  comments: [
    { type: 'maxLength' as const, value: 1000, message: 'Comments must be less than 1000 characters' }
  ]
};

/**
 * Validate a single field value against validation rules
 */
function validateField(value: any, rules: ValidationRule[]): string | null {
  for (const rule of rules) {
    switch (rule.type) {
      case 'required':
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          return rule.message || 'This field is required';
        }
        break;
      
      case 'email':
        if (value && typeof value === 'string') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value.trim())) {
            return rule.message || 'Please enter a valid email address';
          }
        }
        break;
      
      case 'minLength':
        if (value && typeof value === 'string' && typeof rule.value === 'number') {
          if (value.trim().length < rule.value) {
            return rule.message || `Must be at least ${rule.value} characters`;
          }
        }
        break;
      
      case 'maxLength':
        if (value && typeof value === 'string' && typeof rule.value === 'number') {
          if (value.trim().length > rule.value) {
            return rule.message || `Must be less than ${rule.value} characters`;
          }
        }
        break;
      
      case 'pattern':
        if (value && typeof value === 'string' && typeof rule.value === 'string') {
          const regex = new RegExp(rule.value);
          if (!regex.test(value)) {
            return rule.message || 'Invalid format';
          }
        }
        break;
      
      case 'custom':
        if (rule.validator) {
          const result = rule.validator(value);
          if (result !== true) {
            return typeof result === 'string' ? result : rule.message || 'Validation failed';
          }
        }
        break;
    }
  }
  
  return null;
}

/**
 * Validate lead form data
 */
export function validateLeadData(
  data: LeadFormData, 
  customRules?: { [field: string]: ValidationRule[] }
): ValidationErrors | null {
  const errors: ValidationErrors = {};
  const rules = { ...DEFAULT_VALIDATION_RULES, ...customRules };
  
  // Validate each field that has rules
  Object.entries(rules).forEach(([field, fieldRules]) => {
    const value = (data as any)[field];
    const error = validateField(value, fieldRules);
    if (error) {
      errors[field] = error;
    }
  });
  
  // Return null if no errors, otherwise return errors object
  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * Validate a single field - useful for real-time validation
 */
export function validateSingleField(
  field: string,
  value: any,
  customRules?: { [field: string]: ValidationRule[] }
): string | null {
  const rules = { ...DEFAULT_VALIDATION_RULES, ...customRules };
  const fieldRules = rules[field as keyof typeof rules];
  
  if (!fieldRules) {
    return null;
  }
  
  return validateField(value, fieldRules);
}

/**
 * Check if email format is valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Check if phone number format is valid (basic validation)
 */
export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[+]?[\d\s\-\(\)]+$/;
  return phoneRegex.test(phone.trim()) && phone.replace(/\D/g, '').length >= 7;
}

/**
 * Sanitize form data - trim strings and remove empty optional fields
 */
export function sanitizeLeadData(data: LeadFormData): LeadFormData {
  const sanitized: LeadFormData = {
    name: typeof data.name === 'string' ? data.name.trim() : '',
    email: typeof data.email === 'string' ? data.email.trim().toLowerCase() : ''
  };
  
  // Add optional fields only if they have values
  if (data.phone && typeof data.phone === 'string' && data.phone.trim()) {
    sanitized.phone = data.phone.trim();
  }
  
  if (data.company && typeof data.company === 'string' && data.company.trim()) {
    sanitized.company = data.company.trim();
  }
  
  if (data.comments && typeof data.comments === 'string' && data.comments.trim()) {
    sanitized.comments = data.comments.trim();
  }
  
  // Preserve any additional fields
  Object.keys(data).forEach(key => {
    if (!['name', 'email', 'phone', 'company', 'comments'].includes(key)) {
      (sanitized as any)[key] = (data as any)[key];
    }
  });
  
  return sanitized;
}